import React, { useEffect } from 'react'
import { createUsePrefParamsHook } from 'core/hooks/useParams'
import { listTablePrefs, TablePrefsParams } from 'app/constants'
import { pick } from 'ramda'
import DateCell from 'core/components/listTable/cells/DateCell'
import DataKeys from 'k8s/DataKeys'
import { routeManager } from 'core/utils/routes'
import DocumentMeta from 'core/components/DocumentMeta'
import { clusterRoleBindingsSelector } from './selectors'
import { ArrayElement } from 'core/actions/Action'
import { listClusterRoleBindings, deleteClusterRoleBinding } from './actions'
import { GridViewColumn } from 'core/elements/grid/Grid'
import useListAction from 'core/hooks/useListAction'
import ListContainer from 'core/containers/ListContainer'
import AddClusterRoleBindingPage from 'k8s/components/access-control/cluster-role-bindings/AddClusterRoleBindingPage'
import UpdateClusterRoleBindingPage from 'k8s/components/access-control/cluster-role-bindings/UpdateClusterRoleBindingPage'
import InferActionParams from 'core/actions/InferActionParams'
import useGlobalParams from 'core/hooks/useGlobalParams'
import { useAppSelector } from 'app/store'
import usePluginRouter from 'core/hooks/usePluginRouter'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { K8sPluginGlobalPrefs } from 'k8s/model'
import { SortingState } from 'core/elements/grid/hooks/useGridSorting'

type ModelDataKey = DataKeys.ClusterRoleBindings
type SelectorModel = ArrayElement<ReturnType<typeof clusterRoleBindingsSelector>>
type ActionParams = InferActionParams<typeof listClusterRoleBindings>

type Params = ActionParams & {
  clusterId?: string
  healthyClusters: boolean
}

const requiredParams: Array<keyof ActionParams> = ['clusterId']

const defaultParams: Params & SortingState = {
  clusterId: null,
  healthyClusters: true,
  orderBy: 'created',
  orderDirection: 'desc',
}

const usePrefParams = createUsePrefParamsHook<Params & TablePrefsParams>(
  'ClusterRoleBindings',
  listTablePrefs,
)

const searchTargets = ['name', 'clusterName']

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'name', label: 'Name' },
  { key: 'created', label: 'Created', render: (value) => <DateCell value={value} /> },
]

export default function ClusterRoleBindingsListPage() {
  const { allParams: params, getParamsUpdater } = useGlobalParams(usePrefParams, defaultParams)
  const { currentPluginId } = usePluginRouter()
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences<K8sPluginGlobalPrefs>(
    'k8sPluginGlobalParams',
  )
  const clusterId = k8sPluginGlobalPerfs?.clusterId || ''
  const clusterName = k8sPluginGlobalPerfs?.cluster || ''

  const { message, loading, reload } = useListAction(listClusterRoleBindings, {
    params: { clusterId },
    requiredParams,
  })
  const data = useAppSelector(clusterRoleBindingsSelector)

  useEffect(() => {
    if (clusterId) {
      reload(true)
    }
  }, [clusterId])

  return (
    <>
      <DocumentMeta title="Cluster Role Bindings" />
      <AddClusterRoleBindingPage data-testid="add-cluster-role-binding-page" />
      <UpdateClusterRoleBindingPage data-testid="update-cluster-role-binding-page" />
      <ListContainer<ModelDataKey, SelectorModel>
        dataKey={DataKeys.ClusterRoleBindings}
        searchTargets={searchTargets}
        uniqueIdentifier="id"
        loading={loading}
        loadingMessage={message}
        onRefresh={reload}
        columns={columns}
        data={data}
        getParamsUpdater={getParamsUpdater}
        addUrl={routeManager.routes.kubernetes.accessControl.addClusterRoleBindings.path({
          cluster: clusterName,
          plugin: currentPluginId,
        })}
        addText="Add Cluster Role Binding"
        editUrl={(item, id) =>
          routeManager.routes.kubernetes.accessControl.editClusterRoleBindings.path({
            id,
            cluster: clusterName,
            plugin: currentPluginId,
          })
        }
        deleteAction={deleteClusterRoleBinding}
        {...pick(listTablePrefs, params)}
        data-testid="cluster-role-bindings-list-container"
      />
    </>
  )
}
